import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Seismograph } from '../../../shared/models/seismograph';
import { ApiResponse } from '../../../shared/models/api-response';
import { ControlPointInstrument } from '../../../shared/models/controlPointInstrument';
import { lastValueFrom } from 'rxjs';
import { Company } from '../../../core/models/company';
import { HttpService } from '../../../core/services/http.service';
import { CompanyInstrument } from '../../../shared/models/companyInstrument';
import { TimeZoneDto } from '../../../shared/models/time-zone.dto';

@Injectable({
    providedIn: 'root'
})
export class SeismographService {
    private baseUrl = environment.vibrationApiURL;
    private cacheSeismographs: Seismograph[] = [];
    private controlPointCache: ControlPointInstrument[] = [];
    private companiesCache: Company[] = [];
    private companyInstrumentCache: Map<number, CompanyInstrument[]> = new Map();

    constructor(private httpClient: HttpClient, private http: HttpService) { }

    async getAllSeismographs(): Promise<Seismograph[]> {
        try {
            const response = await lastValueFrom(
                this.httpClient.get<ApiResponse<Seismograph[]>>(`${this.baseUrl}/seismographs`)
            );
            if (response.success && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching seismographs:', error);
        }
        return [];
    }

    getSeismographById(id: number): Seismograph | undefined {
        return this.cacheSeismographs.find(seismograph => seismograph.id === id);
    }

    async getSeismographBySerialNumber(serialNumber: string): Promise<Seismograph | null> {
        const cachedSeismograph = this.cacheSeismographs.find(s => s.serialNumber === serialNumber);
        if (cachedSeismograph) {
            return cachedSeismograph;
        }
        try {
            const response = await lastValueFrom(
                this.httpClient.get<ApiResponse<Seismograph>>(`${this.baseUrl}/seismographs/serial-number/${serialNumber}`)
            );
            if (response.success && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error getting seismograph:', error);
        }
        return null;
    }

    clearCache(): void {
        this.cacheSeismographs = [];
        this.controlPointCache = [];
        this.companiesCache = [];
    }

    async createSeismograph(seismographDto: Seismograph): Promise<Seismograph> {
        try {
            const response = await lastValueFrom(
                this.httpClient.post<ApiResponse<Seismograph>>(`${this.baseUrl}/seismographs`, seismographDto)
            );
            if (response.success && response.data) {
                this.cacheSeismographs.push(response.data);
                return response.data;
            }
        } catch (error) {
            console.error('Error creating seismograph:', error);
        }
        return null;
    }

    async updateSeismograph(seismograph: Seismograph): Promise<Seismograph | null> {
        try {
            const payload = {
                serialNumber: seismograph.serialNumber,
                lastCalibration: seismograph.lastCalibration,
                isDeleted: seismograph.isDeleted,
                isFavorite: seismograph.isFavorite,
                companyId: seismograph.companyId !== undefined ? Number(seismograph.companyId) : null,
                siteId: seismograph.siteId !== undefined ? Number(seismograph.siteId) : null,
                timeZoneId: seismograph.timeZoneId !== undefined ? Number(seismograph.timeZoneId) : null,
            };
            var t = JSON.stringify(payload);
            console.log('Sending:', payload);
            const response = await lastValueFrom(
                this.httpClient.put<ApiResponse<Seismograph>>(`${this.baseUrl}/seismographs`, payload)
            );
            if (response.success && response.data) {
                const index = this.cacheSeismographs.findIndex(s => s.serialNumber === seismograph.serialNumber);
                if (index !== -1) {
                    this.cacheSeismographs[index] = response.data;
                }
                return response.data;
            }
        } catch (error) {
            console.error('Error updating seismograph:', error);
        }
        return null;
    }

    async getTimeZones(): Promise<TimeZoneDto[]> {
        try {
            const response = await lastValueFrom(
                this.httpClient.get<ApiResponse<TimeZoneDto[]>>(`${this.baseUrl}/seismographs/time-zones`)
            );
            if (response.success && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching time zones:', error);
        }
        return [];
    }

    async deleteSeismograph(id: number): Promise<boolean> {
        try {
            const response = await lastValueFrom(
                this.httpClient.delete<ApiResponse<null>>(`${this.baseUrl}/seismographs/${id}`)
            );
            if (response.success) {
                this.cacheSeismographs = this.cacheSeismographs.filter(s => s.id !== id);
                return true;
            }
        } catch (error) {
            console.error('Error deleting seismograph:', error);
        }
        return false;
    }

    async createControlPointInstrument(controlPointId: number, seismographId: number, controlPointName?: string): Promise<boolean> {
        const url = `${this.baseUrl}/seismographs/control-point/${controlPointId}/seismograph/${seismographId}`;
        try {
            const response = await lastValueFrom(
                this.httpClient.post<ApiResponse<boolean>>(url, { controlPointName: controlPointName ?? null })
            );
            if (response.success && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error creating ControlPointInstrument:', error);
        }
        return false;
    }

    async getSeismographsByControlPointIds(controlPointIds: number[]): Promise<ControlPointInstrument[]> {
        if (!controlPointIds || controlPointIds.length === 0) {
            console.warn('No Control Point IDs provided.');
            return [];
        }

        const idsAsQuery = controlPointIds.map(id => `id=${id}`).join('&');
        const url = `${this.baseUrl}/seismographs/control-points-instruments?${idsAsQuery}`;

        try {
            const response = await lastValueFrom(
                this.httpClient.get<ApiResponse<ControlPointInstrument[]>>(url)
            );
            if (response.success && Array.isArray(response.data)) {
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching ControlPointInstruments:', error);
        }
        return [];
    }

    async getControlPointInstrumentsBySeismographsId(seismographsIds: number[]): Promise<ControlPointInstrument[]> {
        if (!seismographsIds || seismographsIds.length === 0) {
            console.warn('No seismograph serial number provided.');
            return [];
        }

        const idsAsQuery = seismographsIds.map(id => `id=${id}`).join('&');
        const url = `${this.baseUrl}/seismographs/control-points-instruments/ids?${idsAsQuery}`;

        try {
            const response = await lastValueFrom(
                this.httpClient.get<ApiResponse<ControlPointInstrument[]>>(url)
            );
            if (response.success && Array.isArray(response.data)) {
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching ControlPointInstruments:', error);
        }
        return [];
    }

    async deleteControlPointInstrument(controlPointId: number, seismographId: number): Promise<boolean> {
        const url = `${this.baseUrl}/seismographs/control-point/${controlPointId}/seismograph/${seismographId}`;
        try {
            const response = await lastValueFrom(
                this.httpClient.delete<ApiResponse<boolean>>(url)
            );
            if (response.success) {
                this.controlPointCache = this.controlPointCache.filter(cp => cp.controlPointId !== controlPointId);
                return true;
            }
        } catch (error) {
            console.error('Error deleting ControlPointInstrument:', error);
        }
        return false;
    }

    async getAllCompanies(): Promise<Company[]> {
        if (this.companiesCache.length > 0) {
            return this.companiesCache;
        }

        try {
            const response = await lastValueFrom(
                this.http.get<Company[]>(`companies/by-region`)
            );
            if (response) {
                this.companiesCache = response;
                return response;
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
        return [];
    }

    async createCompanyInstrument(companyId: number, seismographId: number): Promise<boolean> {
        const url = `${this.baseUrl}/seismographs/company-instrument/${companyId}/seismograph/${seismographId}`;

        try {
            const response = await lastValueFrom(
                this.httpClient.post<ApiResponse<boolean>>(url, null)
            );

            if (response.success && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error creating CompanyInstrument:', error);
        }

        return false;
    }

    async getCompanyInstrumentsBySeismographIds(seismographIds: number[]): Promise<CompanyInstrument[]> {
        if (!seismographIds || seismographIds.length === 0) {
            console.warn('No Seismograph IDs provided.');
            return [];
        }

        const cachedResults: CompanyInstrument[] = [];
        const idsToFetch: number[] = [];

        seismographIds.forEach(id => {
            if (this.companyInstrumentCache.has(id)) {
                cachedResults.push(...this.companyInstrumentCache.get(id)!);
            } else {
                if (id) {
                    idsToFetch.push(id);
                }
            }
        });

        if (idsToFetch.length === 0) {
            return cachedResults;
        }

        const url = `${this.baseUrl}/seismographs/company-instruments`;
        let params = new HttpParams();
        idsToFetch.forEach(id => params = params.append('seismographIds', id.toString()));

        try {
            const response = await lastValueFrom(
                this.httpClient.get<ApiResponse<CompanyInstrument[]>>(url, { params })
            );

            if (response.success && Array.isArray(response.data)) {
                response.data.forEach(instrument => {
                    if (!this.companyInstrumentCache.has(instrument.seismographId)) {
                        this.companyInstrumentCache.set(instrument.seismographId, []);
                    }
                    this.companyInstrumentCache.get(instrument.seismographId)!.push(instrument);
                });

                return [...cachedResults, ...response.data];
            }
        } catch (error) {
            console.error('Error fetching CompanyInstruments:', error);
        }

        return cachedResults;
    }
}
